import { get, filter, map } from 'lodash';
import { custom } from '../../utils/Api';

/**
 * Custom hook for managing clock card entries API calls
 * Provides functions to create and update clock card entries
 *
 * Important: UseAccrualsIfNeeded should always be set to false in the first request.
 * If the API response status is "WARNING" when registering holiday, it means that accruals will be taken from
 * the employee's holiday accrual (ávinnsla) balance. The user must confirm this action by sending another request
 * with UseAccrualsIfNeeded set to true.
 */
const useClockCardEntries = () => {
  /**
   * Creates new clock card entries
   * @param {Object} data - Clock card entry data
   * @param {number} data.EmployeeDetailID - Employee detail ID
   * @param {number} data.ClockCardEntryTypeID - Entry type ID
   * @param {string} data.Comment - Entry comment
   * @param {string} data.PeriodBegda - Start date/time (ISO string)
   * @param {string} data.PeriodEndda - End date/time (ISO string)
   * @param {number} data.HoursPerDay - Hours per day
   * @param {boolean} data.IncludesWeekends - Include weekends flag
   * @param {boolean} data.IncludesRedDays - Include red days flag
   * @param {boolean} data.UseAccrualsIfNeeded - Use accruals if needed. Set to false for initial request.
   *                                             If response status is "WARNING", user must confirm and resend with true.
   * @returns {Promise<Object|null>} Response with status and messages, or null on error
   */
  const createClockCardEntries = async (data) => {
    const requestConfig = {
      url: '/kjarni/api/v2/ClockCardEntries/CreateClockCardEntries',
      method: 'POST',
      data,
    };
    try {
      const res = get(await custom(requestConfig), 'data');
      const status = get(res, 'Status');
      const messages = filter(get(res, 'Messages'), m => m.Language === (localStorage.language === 'is' ? 'IS' : 'EN'));
      return {
        status,
        messages: map(messages, 'Message'),
      };
    } catch (error) {
      console.log('Error during clock card entry request', error);
      return null;
    }
  };

  /**
   * Updates existing clock card entries
   * @param {Object} data - Update data
   * @param {number[]} data.ClockCardEntryIDs - Array of entry IDs to update
   * @param {number} data.ClockCardEntryTypeID - New entry type ID
   * @param {string} data.ClockCardEntryComment - New comment
   * @param {string} data.PeriodBegda - New clock in time (ISO string)
   * @param {string} data.PeriodEndda - New clock out time (ISO string)
   * @param {boolean} data.IncludesWeekends - Include weekends flag
   * @param {boolean} data.IncludesRedDays - Include red days flag
   * @param {boolean} data.UseAccrualsIfNeeded - Use accruals if needed. Set to false for initial request.
   *                                             If response status is "WARNING", user must confirm and resend with true.
   * @returns {Promise<Object|null>} Response with status and messages, or null on error
   */
  const updateClockCardEntries = async (data) => {
    const requestConfig = {
      url: '/kjarni/api/v2/ClockCardEntries/UpdateClockCardEntries',
      method: 'POST',
      data,
    };
    try {
      const res = get(await custom(requestConfig), 'data');
      const status = get(res, 'Status');
      const messages = filter(get(res, 'Messages'), m => m.Language === (localStorage.language === 'is' ? 'IS' : 'EN'));
      return {
        status,
        messages: map(messages, 'Message'),
      };
    } catch (error) {
      console.log('Error during clock card entry request', error);
      return null;
    }
  };

  return {
    createClockCardEntries,
    updateClockCardEntries,
  };
};

export default useClockCardEntries;



// WEBPACK FOOTER //
// ./src/sites/time/useClockCardEntries.js